import React from 'react';

const Tooth = ({ number, isSelected, onClick }) => (
    <button
        type="button"
        onClick={() => onClick(number)}
        className={`aspect-square flex-1 min-w-5 max-w-10 rounded-full border sm:border-2 text-[8px] xs:text-[9px] sm:text-[10px] md:text-xs lg:text-sm font-medium transition-all duration-150
            ${isSelected
                ? 'bg-yellow-500 border-yellow-400 text-black shadow-[0_0_10px_rgba(250,204,21,0.6)]'
                : 'bg-white/[0.06] border-white/15 text-gray-300 hover:bg-white/15 hover:border-white/30 hover:text-white'
            }`}
    >
        {number}
    </button>
);

// Sort teeth in dental order (quadrant then number)
const sortTeeth = (teeth) => {
    return [...teeth].sort((a, b) => {
        const quadrantA = parseInt(a[0]);
        const quadrantB = parseInt(b[0]);
        if (quadrantA !== quadrantB) return quadrantA - quadrantB;
        const numA = parseInt(a[1]);
        const numB = parseInt(b[1]);
        // For quadrants 1 and 4, sort descending; for 2 and 3, sort ascending
        if (quadrantA === 1 || quadrantA === 4) return numB - numA;
        return numA - numB;
    });
};

const ToothSelector = ({ value = [], onChange, name = 'toothNumbers', disabled = false, required = false, className = '' }) => {
    const upperRight = ['18','17','16','15','14','13','12','11'];
    const upperLeft = ['21','22','23','24','25','26','27','28'];
    const lowerRight = ['48','47','46','45','44','43','42','41'];
    const lowerLeft = ['31','32','33','34','35','36','37','38'];

    // Ensure value is always an array
    const selectedTeeth = Array.isArray(value) ? value : (value ? [value] : []);

    const handleClick = (t) => {
        if (disabled) return;
        const newValue = selectedTeeth.includes(t)
            ? selectedTeeth.filter(tooth => tooth !== t)
            : [...selectedTeeth, t];
        onChange?.({ target: { name, value: newValue } });
    };

    const isSelected = (t) => selectedTeeth.includes(t);

    return (
        <div className={`${disabled ? 'opacity-50 pointer-events-none' : ''} ${className}`.trim()}>
            <div className="bg-black/40 border border-white/10 rounded-xl p-2 xs:p-3 sm:p-4 lg:p-6 h-full flex flex-col justify-center overflow-x-auto" style={{ scrollbarWidth: 'thin', scrollbarColor: '#4b5563 transparent' }}>
                {/* Upper jaw */}
                <div className="flex justify-center items-center gap-px xs:gap-0.5 sm:gap-1 lg:gap-1.5 min-w-[340px]">
                    <div className="flex flex-1 justify-end gap-px xs:gap-0.5 sm:gap-1 lg:gap-1.5">
                        {upperRight.map(n => <Tooth key={n} number={n} isSelected={isSelected(n)} onClick={handleClick} />)}
                    </div>
                    <div className="w-px self-stretch bg-white/20 mx-0.5 xs:mx-1 sm:mx-1.5 lg:mx-2 flex-shrink-0" />
                    <div className="flex flex-1 justify-start gap-px xs:gap-0.5 sm:gap-1 lg:gap-1.5">
                        {upperLeft.map(n => <Tooth key={n} number={n} isSelected={isSelected(n)} onClick={handleClick} />)}
                    </div>
                </div>

                {/* Separator */}
                <div className="h-px bg-white/20 my-2 xs:my-3 sm:my-4 lg:my-5 min-w-[340px]" />

                {/* Lower jaw */}
                <div className="flex justify-center items-center gap-px xs:gap-0.5 sm:gap-1 lg:gap-1.5 min-w-[340px]">
                    <div className="flex flex-1 justify-end gap-px xs:gap-0.5 sm:gap-1 lg:gap-1.5">
                        {lowerRight.map(n => <Tooth key={n} number={n} isSelected={isSelected(n)} onClick={handleClick} />)}
                    </div>
                    <div className="w-px self-stretch bg-white/20 mx-0.5 xs:mx-1 sm:mx-1.5 lg:mx-2 flex-shrink-0" />
                    <div className="flex flex-1 justify-start gap-px xs:gap-0.5 sm:gap-1 lg:gap-1.5">
                        {lowerLeft.map(n => <Tooth key={n} number={n} isSelected={isSelected(n)} onClick={handleClick} />)}
                    </div>
                </div>

                {/* Selected teeth display */}
                {selectedTeeth.length > 0 && (
                    <div className="mt-2 xs:mt-3 sm:mt-4 text-center">
                        <span className="text-[10px] xs:text-xs sm:text-sm text-yellow-400">
                            {selectedTeeth.length === 1
                                ? `Dent sélectionnée : ${selectedTeeth[0]}`
                                : `Dents sélectionnées : ${sortTeeth(selectedTeeth).join(', ')}`
                            }
                        </span>
                    </div>
                )}
            </div>

            {required && <input type="text" value={selectedTeeth.length > 0 ? selectedTeeth.join(',') : ''} readOnly required className="sr-only" tabIndex={-1} />}
        </div>
    );
};

export default ToothSelector;
